import { play } from "cuelume";

const MESSAGE_SOUND = "bloom";
const MESSAGE_THROTTLE_MS = 400;
const FULL_VOLUME = 1;
const MESSAGE_LOUDNESS_LAYER = 0.55;

let unlockBound = false;
let lastMessageSoundAt = 0;
let soundsEnabled = true;
const heardMessageIds = new Set<string>();

export function areAppSoundsEnabled(): boolean {
  return soundsEnabled;
}

export function setAppSoundsEnabled(enabled: boolean): void {
  soundsEnabled = enabled !== false;
}

export function rememberHeardChatMessageId(id: unknown): boolean {
  if (id == null) {
    return false;
  }

  const key = String(id);
  if (heardMessageIds.has(key)) {
    return false;
  }

  heardMessageIds.add(key);
  return true;
}

export function resetHeardChatMessageIds(): void {
  heardMessageIds.clear();
}

export function unlockCueLumeAudio(): void {
  if (unlockBound || typeof window === "undefined") {
    return;
  }

  unlockBound = true;

  const unlock = () => {
    play(MESSAGE_SOUND, { volume: 0.00001 });
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
  };

  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
}

export function playMessageArrival(): void {
  if (!soundsEnabled) {
    return;
  }

  const now = Date.now();
  if (now - lastMessageSoundAt < MESSAGE_THROTTLE_MS) {
    return;
  }

  lastMessageSoundAt = now;
  play(MESSAGE_SOUND, { volume: FULL_VOLUME });
  play(MESSAGE_SOUND, { volume: MESSAGE_LOUDNESS_LAYER });
}

export function playNotificationChime(): void {
  if (!soundsEnabled) {
    return;
  }

  play("chime", { volume: FULL_VOLUME });
}

export function isIncomingUserChatMessage(
  message: any,
  currentUserId?: unknown,
): boolean {
  if (!message) {
    return false;
  }

  if (message.system_message) {
    return false;
  }

  if (message.reminder === true) {
    return false;
  }

  if (message.tracked_item != null) {
    return false;
  }

  if (currentUserId != null && message.sent_by?.id == currentUserId) {
    return false;
  }

  return true;
}

export function announceNewIncomingMessages(
  messages: any[],
  seenIds: Set<string>,
  currentUserId: unknown,
  primed: boolean,
): { primed: boolean; play: boolean } {
  const ids = (messages ?? [])
    .map((message) => message?.id)
    .filter((id) => id != null);

  if (!primed) {
    if (ids.length === 0) {
      return { primed: false, play: false };
    }

    ids.forEach((id) => seenIds.add(id));
    return { primed: true, play: false };
  }

  let shouldPlay = false;
  for (const message of messages ?? []) {
    const id = message?.id;
    if (id == null || seenIds.has(id)) {
      continue;
    }

    seenIds.add(id);
    if (isIncomingUserChatMessage(message, currentUserId)) {
      shouldPlay = true;
    }
  }

  return { primed: true, play: shouldPlay };
}
